import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from 'react-router-dom';
import { Zap, Target, Rocket, TrendingUp, Building2 } from "lucide-react";
import { GradientButton } from "@/components/common";

// Static data — defined outside component so it's never recreated
const WORDS = [
    { text: "Disruption",   icon: Zap        },
    { text: "Development",  icon: Rocket     },
    { text: "Solutions",    icon: Target     },
    { text: "Optimization", icon: TrendingUp },
    { text: "Enhancement",  icon: Zap        },
];

const CARDS = [
    {
        gradient: 'from-cyan-500/20 to-blue-500/20',
        iconBg: 'from-cyan-500/20 to-blue-500/20',
        iconBorder: 'border-cyan-500/30',
        Icon: Zap,
        iconColor: 'text-cyan-400',
        title: 'Our Mission',
        body: "A trusted IT provider committed to using technology to drive innovation and success in today's digital world.",
    },
    {
        gradient: 'from-blue-500/20 to-purple-500/20',
        iconBg: 'from-blue-500/20 to-purple-500/20',
        iconBorder: 'border-blue-500/30',
        Icon: Target,
        iconColor: 'text-blue-400',
        title: 'Tailored Solutions',
        body: "Our team delivers exceptional, tailored solutions built around your business's unique challenges and goals.",
    },
    {
        gradient: 'from-purple-500/20 to-pink-500/20',
        iconBg: 'from-purple-500/20 to-pink-500/20',
        iconBorder: 'border-purple-500/30',
        Icon: Rocket,
        iconColor: 'text-purple-400',
        title: 'Innovation First',
        body: "We equip you with the right tools to make an impact, staying ahead of the latest technologies and industry trends.",
    },
    {
        gradient: 'from-cyan-500/20 to-blue-500/20',
        iconBg: 'from-cyan-500/20 to-blue-500/20',
        iconBorder: 'border-cyan-500/30',
        Icon: TrendingUp,
        iconColor: 'text-cyan-400',
        title: 'Long-term Partnership',
        body: "We build lasting client relationships grounded in trust, collaboration, and shared success.",
    },
];

export default function DiscoverFuture() {
    const navigate = useNavigate();
    const [currentWordIndex, setCurrentWordIndex] = useState(0);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        setIsVisible(true);
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentWordIndex(prev => (prev + 1) % WORDS.length);
        }, 3500);
        return () => clearInterval(interval);
    }, []); // WORDS.length is now stable — no dep needed

    // Stable callback — never recreated
    const handleAboutClick = useCallback(() => {
        navigate('/about');
    }, [navigate]);

    // Memoised class string — only recalculates when isVisible flips
    const sectionInnerClass = useMemo(() => (
        `transition-all duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'} translate-y-0`
    ), [isVisible]);

    // Memoised card halves — static data, computed once
    const leftCards  = useMemo(() => CARDS.slice(0, 2), []);
    const rightCards = useMemo(() => CARDS.slice(2, 4), []);

    return (
        <section
            className="relative pt-14 pb-20 md:py-20 overflow-hidden"
            id="about"
            style={{ scrollMarginTop: '100px' }}
        >
            {/* Animated background elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none transform-gpu">
                <div className="absolute top-0 left-1/4 w-64 h-64 bg-cyan-500/5 rounded-full blur-xl animate-pulse"
                    style={{ animationDuration: '6s' }} />
                <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-purple-500/5 rounded-full blur-xl animate-pulse"
                    style={{ animationDuration: '8s', animationDelay: '1s' }} />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[350px] h-[350px] bg-blue-500/3 rounded-full blur-xl transform-gpu" />
            </div>

            {/* Grid pattern */}
            <div className="absolute inset-0 opacity-[0.02] transform-gpu"
                style={{
                    backgroundImage: 'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
                    backgroundSize: '80px 80px',
                }}
            />

            <div className="container relative z-10">
                <div className={sectionInnerClass}>

                    {/* Header with animated word */}
                    <div className="text-center mb-8 md:mb-12">
                        <div className="inline-flex items-center gap-2 px-4 py-2 mb-8 bg-white/5 rounded-full border border-white/10 backdrop-blur-sm">
                            <Building2 className="w-4 h-4 text-cyan-400" />
                            <span className="text-xs md:text-sm font-semibold text-gray-300 tracking-wider uppercase">
                                Innovation Unleashed
                            </span>
                        </div>

                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-0 leading-tight">
                            Discover the Future of
                        </h2>

                        {/* Animated rotating word */}
                        <div className="relative h-12 sm:h-20 flex items-center justify-center overflow-hidden">
                            {WORDS.map((word, index) => {
                                const Icon = word.icon;
                                return (
                                    <div
                                        key={index}
                                        className={`absolute transition-all duration-700 flex items-center gap-2 sm:gap-4 ${
                                            index === currentWordIndex
                                                ? 'opacity-100 translate-y-0 scale-100'
                                                : 'opacity-0 translate-y-8 scale-95'
                                        }`}
                                    >
                                        <Icon
                                            className="hidden sm:block w-8 h-8 md:w-10 md:h-10 text-cyan-400 flex-shrink-0"
                                            strokeWidth={1.5}
                                        />
                                        <span
                                            className="text-3xl sm:text-4xl lg:text-5xl font-bold whitespace-nowrap"
                                            style={{
                                                background: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 50%, #a855f7 100%)',
                                                WebkitBackgroundClip: 'text',
                                                backgroundClip: 'text',
                                                color: 'transparent',
                                            }}
                                        >
                                            {word.text}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Content Grid */}
                    <div className="grid lg:grid-cols-2 gap-8 lg:gap-10 mb-8">
                        {[leftCards, rightCards].map((col, colIdx) => (
                            <div key={colIdx} className="space-y-8 flex flex-col">
                                {col.map((card, i) => {
                                    const { Icon } = card;
                                    return (
                                        <div key={i} className="relative group flex-1">
                                            <div className={`absolute -inset-1 bg-gradient-to-r ${card.gradient} rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                                            <div className="relative bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-sm hover:bg-white/8 transition-all duration-500 h-full flex flex-col">
                                                <div className="flex items-center gap-3 mb-3">
                                                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${card.iconBg} flex items-center justify-center border ${card.iconBorder} flex-shrink-0`}>
                                                        <Icon className={`w-5 h-5 ${card.iconColor}`} />
                                                    </div>
                                                    <h3 className="text-base md:text-lg font-bold text-white">{card.title}</h3>
                                                </div>
                                                <p className="text-sm text-gray-300 leading-relaxed">{card.body}</p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>

                    {/* CTA */}
                    <div className="text-center">
                        <div className="inline-block relative group">
                            <GradientButton onClick={handleAboutClick}>
                                Explore More
                            </GradientButton>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                @keyframes float {
                    0%, 100% { transform: translateY(0px); }
                    50% { transform: translateY(-20px); }
                }
            `}</style>
        </section>
    );
}